import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

import type { Dashboard, FeedItem } from "../types";
import { CATEGORY, clock, FeedIcon, PendingIcon } from "./FeedIcon";
import { titleSizedButtonSx } from "./SignOff";

const Root = styled(Paper)(({ theme }) => ({ padding: theme.spacing(2, 2.5) }));

const Heading = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));

const Title = styled(Typography)(() => ({ fontSize: "1rem", fontWeight: 700 }));

const List = styled(Box)(() => ({ display: "flex", flexDirection: "column" }));

/** One step: icon column (with the connector line to the next step), time,
 *  then what happened and who recorded it. */
const Step = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "26px 44px minmax(0, 1fr)",
  columnGap: theme.spacing(1.5),
  position: "relative",
  paddingBottom: theme.spacing(1.5),
  // The connector: from under this icon to the next one.
  "&:not(:last-of-type)::before": {
    content: '""',
    position: "absolute",
    left: 12,
    top: 28,
    bottom: 0,
    width: 2,
    background: theme.palette.divider,
  },
}));

const Time = styled(Typography)(() => ({
  fontSize: "0.875rem",
  fontWeight: 600,
  lineHeight: "26px",
  fontVariantNumeric: "tabular-nums",
}));

const What = styled(Box)(() => ({ minWidth: 0, paddingTop: 3 }));

const StepTitle = styled(Typography)(() => ({ fontSize: "0.875rem", fontWeight: 700, lineHeight: 1.35 }));

const Sub = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  overflowWrap: "anywhere",
}));

const Empty = styled(Typography)(({ theme }) => ({ color: theme.palette.text.secondary, fontSize: "0.875rem" }));

/** How many of the latest steps the panel shows; "View full" shows them all in a dialog. */
const COLLAPSED = 8;

type Pending = { at: string | null; title: string; detail: string };

/** The steps, newest first, under the step still pending. */
function Steps({ steps, pending }: { steps: FeedItem[]; pending: Pending | null }) {
  return (
    <List>
      {pending ? (
        <Step>
          <PendingIcon />
          <Time>{pending.at ? clock(pending.at) : ""}</Time>
          <What>
            <StepTitle>{pending.title}</StepTitle>
            <Sub>{pending.detail}</Sub>
          </What>
        </Step>
      ) : null}
      {steps.map((item) => (
        <Step key={item.id}>
          <FeedIcon category={item.category} icon={item.icon} />
          <Time title={new Date(item.at).toLocaleString()}>{clock(item.at)}</Time>
          <What>
            <StepTitle>{item.title}</StepTitle>
            {item.detail ? <Sub>{item.detail}</Sub> : null}
            <Sub>{CATEGORY[item.category].label}</Sub>
          </What>
        </Step>
      ))}
    </List>
  );
}

type MissionTimelineProps = {
  dashboard: Dashboard;
};

/**
 * The mission's key moments, newest first, under the step still pending.
 * Built server-side from recorded facts only (dashboard.milestones, oldest
 * first): every reading after the first of each vital lives in Recent Updates
 * instead.
 */
export function MissionTimeline(props: MissionTimelineProps) {
  const { dashboard } = props;
  const [full, setFull] = useState(false);
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("sm"));

  const all = [...dashboard.milestones].reverse();

  // The next step, when the record says what it is.
  let pending: Pending | null = null;
  if (dashboard.eta_minutes !== null && dashboard.phase !== "complete") {
    const arrival = new Date(new Date(dashboard.updated_at).getTime() + dashboard.eta_minutes * 60000);
    pending = { at: arrival.toISOString(), title: "Arrival pending", detail: `ETA ${dashboard.eta_minutes} min` };
  } else if (dashboard.phase !== "complete" && all.length > 0) {
    pending = { at: null, title: "In progress", detail: "No destination or ETA recorded yet" };
  }

  return (
    <Root>
      <Heading>
        <Title>Mission Timeline</Title>
        {all.length > COLLAPSED ? (
          <Tooltip title={`All ${all.length} steps`} describeChild>
            <Button variant="contained" size="small" onClick={() => setFull(true)} sx={titleSizedButtonSx}>
              View full
            </Button>
          </Tooltip>
        ) : null}
      </Heading>

      {all.length === 0 ? (
        <Empty>Nothing recorded yet.</Empty>
      ) : (
        <Steps steps={all.slice(0, COLLAPSED)} pending={pending} />
      )}

      <Dialog open={full} onClose={() => setFull(false)} maxWidth="sm" fullWidth fullScreen={narrow} scroll="paper">
        <DialogTitle>Mission timeline · {all.length} steps</DialogTitle>
        <DialogContent dividers>
          <Steps steps={all} pending={pending} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFull(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
}
